import React, { useRef, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import type { MetricMeta, ChatExample, ChatMessage } from "../../constants/metricMeta";
import type { MetricSample } from "../../hooks/useMetricSamples";

// =============================================================================
// METRIC TOOLTIP
// =============================================================================
// PURPOSE: Portal-based tooltip showing metric formula, column definitions, and 5 real samples.
//
// REQUIREMENTS:
// - Portal rendering to document.body for z-index control
// - Auto-positions above/below based on viewport space
// - Shows metric formula, column definitions, and up to 5 samples
// - Chat examples for DM metrics (lead/setter message bubbles)
// - Scrollable sample list if content overflows
// =============================================================================

/** Renders a single chat example with message bubbles */
const ChatExampleView: React.FC<{ example: ChatExample }> = ({ example }) => (
  <div className="mb-2 last:mb-0">
    <p className="token-10 text-glass-secondary mb-1">
      {example.label} <span className="text-glass-muted">— {example.contact}</span>
    </p>
    <div className="space-y-1 pl-1 border-l-2 border-black/[0.08]">
      {example.messages.map((msg: ChatMessage, i: number) => (
        <div
          key={i}
          className={`token-11 px-2 py-1 rounded ${
            msg.sender === "lead"
              ? "bg-blue-500/20 text-blue-300 ml-0 mr-4"
              : msg.sender === "setter"
                ? "bg-green-500/20 text-green-300 ml-4 mr-0"
                : "bg-black/[0.03] text-glass-muted italic ml-2 mr-2"
          }`}
        >
          <span className="token-9 font-medium opacity-60">
            {msg.sender === "lead" ? "👤 Lead" : msg.sender === "setter" ? "💬 Setter" : ""}
          </span>
          {msg.sender !== "system" && <br />}
          {msg.text}
        </div>
      ))}
    </div>
  </div>
);

interface MetricTooltipProps {
  meta: MetricMeta;
  samples: MetricSample[];
  anchorRect: DOMRect;
  tooltipKey?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const MetricTooltip: React.FC<MetricTooltipProps> = ({
  meta,
  samples,
  anchorRect,
  tooltipKey,
  onMouseEnter,
  onMouseLeave,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, opacity: 0 });

  const reposition = useCallback((rect: DOMRect) => {
    const el = tooltipRef.current;
    if (!el) return;
    const GAP = 8;
    const tooltipW = el.offsetWidth;
    const tooltipH = el.offsetHeight;

    let top = rect.bottom + GAP;
    let left = rect.left + rect.width / 2 - tooltipW / 2;

    // Flip up if overflows bottom
    if (top + tooltipH > window.innerHeight - 16) {
      top = rect.top - tooltipH - GAP;
    }
    // Clamp horizontal
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipW - 8));
    // Clamp vertical
    top = Math.max(8, top);

    setPos({ top, left, opacity: 1 });
  }, []);

  useLayoutEffect(() => {
    reposition(anchorRect);
  }, [anchorRect, reposition]);

  // Reposition on window resize
  useLayoutEffect(() => {
    const handler = () => reposition(anchorRect);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [anchorRect, reposition]);

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        opacity: pos.opacity,
        zIndex: 9999,
      }}
      className="tt-surface w-80 p-4 text-sm transition-opacity duration-150"
      data-tooltip-key={tooltipKey}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Formula */}
      <div className="mb-3">
        <p className="token-14s text-glass-muted mb-1.5">How it's calculated</p>
        <p className="text-glass font-mono text-xs leading-relaxed">{meta.formula}</p>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {meta.columns.map((col) => (
            <span
              key={col}
              className="inline-block bg-blue-500/10 text-blue-400 token-10 font-mono px-1.5 py-0.5 rounded"
            >
              {col}
            </span>
          ))}
        </div>
        {meta.filterDescription && (
          <p className="token-10 text-glass-muted mt-1.5">{meta.filterDescription}</p>
        )}
      </div>

      <div className="border-t border-black/[0.08] my-2" />

      {/* Chat Examples (for DM metrics) */}
      {meta.chatExamples && meta.chatExamples.length > 0 && (
        <>
          <div>
            <p className="token-14s text-glass-muted mb-2">Example</p>
            {meta.chatExamples.map((example, i) => (
              <ChatExampleView key={i} example={example} />
            ))}
          </div>
          <div className="border-t border-black/[0.08] my-2" />
        </>
      )}

      {/* Last 5 (for non-DM metrics with sample data) */}
      {(!meta.chatExamples || meta.chatExamples.length === 0) && (
        <div>
          <p className="token-14s text-glass-muted mb-2">
            {samples.length > 0
              ? `Last ${samples.length} entr${samples.length === 1 ? "y" : "ies"}`
              : "Recent data"}
          </p>
          {samples.length === 0 ? (
            <p className="text-xs text-glass-muted italic">No data points in current range</p>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full min-w-max text-xs">
                <tbody>
                  {samples.map((s, i) => (
                    <tr key={i}>
                      <td className="text-glass-secondary whitespace-nowrap pr-3 py-0.5">
                        {s.label}
                      </td>
                      <td className="font-medium text-glass whitespace-nowrap pr-3 py-0.5">
                        {s.value}
                      </td>
                      <td className="text-glass-muted token-11 whitespace-nowrap text-right py-0.5">
                        {s.date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};

export default MetricTooltip;
